import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    RadioGroup,
    Radio,
    FormControlLabel,
    FormLabel,
    IconButton,
    InputAdornment,
    Checkbox,
    FormGroup,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Delete as DeleteIcon, Add as AddIcon } from '@material-ui/icons'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'

import {
    GUEST_CARD_FIELD_QUESTIONS,
    isGuestCardFieldRequired,
    guestCardDefaultMoveInDate,
    guestCardDefaultMoveOutDate,
} from '../../../../models/Outreach/services'
import { useGuestCardForm } from '../../../../hooks/Outreach/useGuestCardForm'
import { School, schoolDisplayName } from '../../../../models/types'
import { GuestCardCustomQuestion } from '../../../../models/Outreach/types'
import { GuestCardPremiumQuestion } from './GuestCardPremiumQuestion'

const useStyles = makeStyles((theme: Theme) => ({
    pageContainer: {
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(4, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    formCard: {
        maxWidth: '600px',
        width: '100%',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        borderRadius: '12px',
    },
    cardHeader: {
        padding: theme.spacing(3),
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
    },
    cardTitle: {
        fontSize: '1.75rem',
        fontWeight: 700,
        color: '#1f2937',
        marginBottom: theme.spacing(0.5),
    },
    cardSubtitle: {
        fontSize: '0.875rem',
        color: '#6b7280',
    },
    formContent: {
        padding: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
        },
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#374151',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1.5),
        paddingBottom: theme.spacing(1),
        borderBottom: '1px solid #e5e7eb',
    },
    submitButton: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(1.5),
        borderRadius: '8px',
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: 600,
        background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
        '&:hover': {
            background: 'linear-gradient(135deg, #2563eb, #1e40af)',
        },
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: theme.spacing(2),
    },
    errorContainer: {
        padding: theme.spacing(4),
        textAlign: 'center',
    },
}))

interface CustomQuestionFieldProps {
    question: GuestCardCustomQuestion
    required: boolean
    value: string
    onChange: (value: string) => void
    className?: string
}

const CustomQuestionField: React.FC<CustomQuestionFieldProps> = ({
    question,
    required: isRequired,
    value,
    onChange,
    className,
}) => {
    const questionType = question.question_type
    const label = question.question + (isRequired ? ' *' : '')

    switch (questionType) {
        case 'LONG_TEXT':
            return (
                <TextField
                    className={className}
                    fullWidth
                    label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    variant="outlined"
                    required={isRequired}
                    multiline
                    rows={4}
                />
            )

        case 'DROPDOWN':
            return (
                <FormControl
                    className={className}
                    fullWidth
                    variant="outlined"
                    required={isRequired}
                >
                    <InputLabel>{question.question}</InputLabel>
                    <Select
                        value={value}
                        onChange={(e) => onChange(e.target.value as string)}
                        label={question.question}
                    >
                        <MenuItem value="">
                            <em>Select an option</em>
                        </MenuItem>
                        {question.options?.map((option) => (
                            <MenuItem key={option.id} value={option.label}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )

        case 'MULTIPLE_CHOICE': {
            const selectedValues = value ? value.split('||') : []
            const handleToggle = (optionLabel: string) => {
                const newSelected = selectedValues.includes(optionLabel)
                    ? selectedValues.filter((v) => v !== optionLabel)
                    : [...selectedValues, optionLabel]
                onChange(newSelected.join('||'))
            }
            return (
                <FormControl
                    className={className}
                    fullWidth
                    component="fieldset"
                    required={isRequired}
                >
                    <FormLabel component="legend">{label}</FormLabel>
                    <FormGroup>
                        {question.options?.map((option) => (
                            <FormControlLabel
                                key={option.id}
                                control={
                                    <Checkbox
                                        checked={selectedValues.includes(
                                            option.label,
                                        )}
                                        onChange={() =>
                                            handleToggle(option.label)
                                        }
                                        color="primary"
                                    />
                                }
                                label={option.label}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            )
        }

        case 'YES_NO':
            return (
                <FormControl
                    className={className}
                    fullWidth
                    component="fieldset"
                    required={isRequired}
                >
                    <FormLabel component="legend">{label}</FormLabel>
                    <RadioGroup
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        row
                    >
                        <FormControlLabel
                            value="Yes"
                            control={<Radio color="primary" />}
                            label="Yes"
                        />
                        <FormControlLabel
                            value="No"
                            control={<Radio color="primary" />}
                            label="No"
                        />
                    </RadioGroup>
                </FormControl>
            )

        case 'DATE':
            return (
                <TextField
                    className={className}
                    fullWidth
                    label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    variant="outlined"
                    required={isRequired}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                />
            )

        case 'NUMBER':
            return (
                <TextField
                    className={className}
                    fullWidth
                    label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    variant="outlined"
                    required={isRequired}
                    type="number"
                />
            )

        case 'SHORT_TEXT':
        default:
            return (
                <TextField
                    className={className}
                    fullWidth
                    label={label}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    variant="outlined"
                    required={isRequired}
                />
            )
    }
}

export const GuestCardForm: React.FC = () => {
    const classes = useStyles()
    const theme = useTheme()
    const { token } = useParams<{ token: string }>()
    const {
        formData: apiData,
        isLoading,
        error,
        submitGuestCardForm,
    } = useGuestCardForm(token)
    const schoolList = apiData?.schools ?? []

    // Form state - will hold all form values
    const [formData, setFormData] = useState<Record<string, string>>({})

    // Roommate state - track if roommates are needed and the list of roommates
    const [hasRoommates, setHasRoommates] = useState<string>('no')
    const [roommates, setRoommates] = useState<
        Array<{ name: string; email: string; phone: string }>
    >([])
    const [selectedPremiumIds, setSelectedPremiumIds] = useState<number[]>([])
    const [premiumsNone, setPremiumsNone] = useState(false)

    // Track if form has been submitted successfully
    const [isSubmitted, setIsSubmitted] = useState(false)

    // Phone normalization function
    const normalizePhone = (value: string): string => {
        // Remove all non-digit characters
        const digits = value.replace(/\D/g, '')

        // Format as (XXX) XXX-XXXX
        if (digits.length <= 3) {
            return digits
        } else if (digits.length <= 6) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
        } else {
            return `(${digits.slice(0, 3)}) ${digits.slice(
                3,
                6,
            )}-${digits.slice(6, 10)}`
        }
    }

    // Pre-fill form with prospect data if available
    useEffect(() => {
        if (apiData) {
            const initialData: Record<string, string> = {}
            if (apiData.name) initialData.name = apiData.name
            if (apiData.email) {
                initialData.email = apiData.email
            }
            if (apiData.phone) {
                initialData.phone = normalizePhone(apiData.phone)
            }
            const moveIn = guestCardDefaultMoveInDate(apiData)
            if (moveIn) {
                initialData.preferred_move_in_date = moveIn
            }
            const moveOut = guestCardDefaultMoveOutDate(apiData)
            if (moveOut) {
                initialData.preferred_move_out_date = moveOut
            }
            setFormData(initialData)
        }
    }, [apiData])

    const guestCard = apiData?.guest_card
    const fieldRequired = (field: string) =>
        isGuestCardFieldRequired(guestCard, field)

    // Get enabled contact fields (name, email, phone)
    const getContactFields = (): Array<
        keyof typeof GUEST_CARD_FIELD_QUESTIONS
    > => {
        if (!guestCard) return []
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (guestCard.name) fields.push('name')
        if (guestCard.email) fields.push('email')
        if (guestCard.phone) fields.push('phone')
        return fields
    }

    // Get enabled additional fields (everything except name, email, phone)
    const getAdditionalFields = (): Array<
        keyof typeof GUEST_CARD_FIELD_QUESTIONS
    > => {
        if (!guestCard) return []
        const fields: Array<keyof typeof GUEST_CARD_FIELD_QUESTIONS> = []
        if (guestCard.preferred_unit_config)
            fields.push('preferred_unit_config')
        if (guestCard.preferred_premiums) fields.push('preferred_premiums')
        if (guestCard.preferred_rent_cost) fields.push('preferred_rent_cost')
        if (guestCard.preferred_move_in_date)
            fields.push('preferred_move_in_date')
        if (guestCard.preferred_move_out_date)
            fields.push('preferred_move_out_date')
        if (guestCard.preferred_roommates) fields.push('preferred_roommates')
        if (guestCard.school_attendance) fields.push('school_attendance')
        if (guestCard.additional_requests) fields.push('additional_requests')
        return fields
    }

    const handleFieldChange = (fieldName: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }))
    }

    // Prepare submission data for backend
    const prepareSubmissionData = () => {
        if (!guestCard) return null

        const submissionData: Record<string, any> = {}

        // Get all enabled fields and include them in submission (even if empty)
        const contactFields = getContactFields()
        const additionalFields = getAdditionalFields()

        // Contact fields
        contactFields.forEach((field) => {
            const fieldName = field as string
            submissionData[fieldName] = formData[fieldName] || ''
        })

        // Additional fields
        additionalFields.forEach((field) => {
            const fieldName = field as string

            if (fieldName === 'preferred_roommates') {
                // Convert roommates array to additional_occupants format
                const additionalOccupants = roommates
                    .filter((r) => r.name || r.email || r.phone) // Only include non-empty entries
                    .map((r) => ({
                        name: r.name || '',
                        email: r.email || '',
                        phone: r.phone || '',
                    }))
                submissionData.additional_occupants = additionalOccupants
            } else if (fieldName === 'school_attendance') {
                // School should be the ID (already stored as string ID in formData)
                const schoolId = formData[fieldName]
                    ? parseInt(formData[fieldName], 10)
                    : null
                submissionData.school = schoolId
            } else if (fieldName === 'preferred_unit_config') {
                // Floor plan should be the ID (already stored as string ID in formData)
                const unitConfigId = formData[fieldName]
                    ? parseInt(formData[fieldName], 10)
                    : null
                submissionData.floor_plan = unitConfigId
            } else if (fieldName === 'preferred_premiums') {
                submissionData.preferred_premiums = premiumsNone
                    ? []
                    : selectedPremiumIds
            } else {
                // All other fields
                submissionData[fieldName] = formData[fieldName] || ''
            }
        })

        // Custom questions - format as array with question ID and answer
        if (guestCard.card_questions && guestCard.card_questions.length > 0) {
            submissionData.custom_questions = guestCard.card_questions.map(
                (cardQuestion) => ({
                    question: cardQuestion.custom_question.id,
                    answer:
                        formData[`custom_${cardQuestion.custom_question.id}`] ||
                        '',
                }),
            )
        } else {
            submissionData.custom_questions = []
        }

        return submissionData
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        const requestBody = prepareSubmissionData()
        if (requestBody) {
            console.log('Request Body:', JSON.stringify(requestBody, null, 2))
            try {
                await submitGuestCardForm.mutateAsync(requestBody)
                setIsSubmitted(true)
            } catch (error) {
                // Error is already handled by the mutation's onError
            }
        }
    }

    if (isLoading) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent>
                        <Box className={classes.loadingContainer}>
                            <CircularProgress />
                            <Typography variant="body2" color="textSecondary">
                                Loading form...
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    // Show success screen if already submitted or if form was just submitted
    if (apiData?.already_submitted || isSubmitted) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent>
                        <Box
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: theme.spacing(4),
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                variant="h4"
                                style={{
                                    marginBottom: theme.spacing(2),
                                    color: '#10b981',
                                    fontWeight: 600,
                                }}
                            >
                                ✓ Form Submitted Successfully!
                            </Typography>
                            <Typography
                                variant="body1"
                                style={{
                                    color: '#6b7280',
                                    marginBottom: theme.spacing(1),
                                }}
                            >
                                Thank you for submitting your information.
                            </Typography>
                            <Typography
                                variant="body2"
                                style={{
                                    color: '#9ca3af',
                                }}
                            >
                                We&apos;ll be in touch with you soon.
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    if (error || !apiData || !guestCard) {
        return (
            <Box className={classes.pageContainer}>
                <Card className={classes.formCard}>
                    <CardContent>
                        <Box className={classes.errorContainer}>
                            <Typography variant="h6" color="error" gutterBottom>
                                Error Loading Form
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {error
                                    ? 'Unable to load the guest card form. Please check the link and try again.'
                                    : 'Guest card not found.'}
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        )
    }

    const contactFields = getContactFields()
    const additionalFields = getAdditionalFields()
    const hasCustomQuestions =
        guestCard.card_questions && guestCard.card_questions.length > 0
    const hasAdditionalInfo = additionalFields.length > 0 || hasCustomQuestions

    return (
        <Box className={classes.pageContainer}>
            <Card className={classes.formCard}>
                <Box className={classes.cardHeader}>
                    <Typography className={classes.cardTitle}>
                        {guestCard.header || apiData.apartment_name}
                    </Typography>
                    <Typography className={classes.cardSubtitle}>
                        {guestCard.body || 'Please fill out the form below'}
                    </Typography>
                </Box>

                <CardContent className={classes.formContent}>
                    <form onSubmit={handleSubmit}>
                        {/* Contact Information Section */}
                        {contactFields.length > 0 && (
                            <>
                                <Typography className={classes.sectionTitle}>
                                    Contact Information
                                </Typography>
                                {contactFields.map((field) => {
                                    const fieldName = field as string
                                    const label =
                                        GUEST_CARD_FIELD_QUESTIONS[field]

                                    // Check if field is auto-filled
                                    const isAutoFilled: boolean =
                                        (fieldName === 'name' &&
                                            !!apiData?.name) ||
                                        (fieldName === 'email' &&
                                            !!apiData?.email) ||
                                        (fieldName === 'phone' &&
                                            !!apiData?.phone)

                                    // Name field - always read-only if auto-filled
                                    if (fieldName === 'name' && isAutoFilled) {
                                        return (
                                            <TextField
                                                key={fieldName}
                                                className={classes.formField}
                                                fullWidth
                                                label={label}
                                                value={
                                                    formData[fieldName] || ''
                                                }
                                                variant="outlined"
                                                required
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                style={{
                                                    backgroundColor: '#f9fafb',
                                                }}
                                            />
                                        )
                                    }

                                    // Email field - read-only if auto-filled from backend
                                    if (fieldName === 'email') {
                                        const isReadOnly: boolean = !!apiData?.email
                                        return (
                                            <TextField
                                                key={fieldName}
                                                className={classes.formField}
                                                fullWidth
                                                label={label}
                                                value={
                                                    formData[fieldName] || ''
                                                }
                                                onChange={(e) => {
                                                    handleFieldChange(
                                                        fieldName,
                                                        e.target.value,
                                                    )
                                                }}
                                                variant="outlined"
                                                required
                                                type="email"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                }}
                                                style={{
                                                    backgroundColor: isReadOnly
                                                        ? '#f9fafb'
                                                        : 'transparent',
                                                }}
                                            />
                                        )
                                    }

                                    // Phone field - read-only if auto-filled from backend
                                    if (fieldName === 'phone') {
                                        const isReadOnly: boolean = !!apiData?.phone
                                        return (
                                            <TextField
                                                key={fieldName}
                                                className={classes.formField}
                                                fullWidth
                                                label={label}
                                                value={
                                                    formData[fieldName] || ''
                                                }
                                                onChange={(e) => {
                                                    const normalized = normalizePhone(
                                                        e.target.value,
                                                    )
                                                    handleFieldChange(
                                                        fieldName,
                                                        normalized,
                                                    )
                                                }}
                                                variant="outlined"
                                                required={fieldRequired(
                                                    'phone',
                                                )}
                                                type="tel"
                                                InputProps={{
                                                    readOnly: isReadOnly,
                                                }}
                                                style={{
                                                    backgroundColor: isReadOnly
                                                        ? '#f9fafb'
                                                        : 'transparent',
                                                }}
                                            />
                                        )
                                    }

                                    // Default text field (shouldn't happen for contact fields, but just in case)
                                    return (
                                        <TextField
                                            key={fieldName}
                                            className={classes.formField}
                                            fullWidth
                                            label={label}
                                            value={formData[fieldName] || ''}
                                            onChange={(e) =>
                                                handleFieldChange(
                                                    fieldName,
                                                    e.target.value,
                                                )
                                            }
                                            variant="outlined"
                                            required
                                            type="text"
                                        />
                                    )
                                })}
                            </>
                        )}

                        {/* Additional Information Section */}
                        {hasAdditionalInfo && (
                            <>
                                <Typography className={classes.sectionTitle}>
                                    Additional Information
                                </Typography>
                                {/* Additional default fields */}
                                {additionalFields
                                    .filter(
                                        (field) =>
                                            field !== 'preferred_roommates',
                                    )
                                    .map((field) => {
                                        const fieldName = field as string
                                        const label =
                                            GUEST_CARD_FIELD_QUESTIONS[field]

                                        // Handle preferred_unit_config as a select dropdown
                                        if (
                                            fieldName ===
                                            'preferred_unit_config'
                                        ) {
                                            return (
                                                <FormControl
                                                    key={fieldName}
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    variant="outlined"
                                                    required={fieldRequired(
                                                        fieldName,
                                                    )}
                                                >
                                                    <InputLabel>
                                                        {label}
                                                    </InputLabel>
                                                    <Select
                                                        value={
                                                            formData[
                                                                fieldName
                                                            ] || ''
                                                        }
                                                        onChange={(e) =>
                                                            handleFieldChange(
                                                                fieldName,
                                                                e.target
                                                                    .value as string,
                                                            )
                                                        }
                                                        label={label}
                                                    >
                                                        {apiData?.unit_configs.map(
                                                            (config) => (
                                                                <MenuItem
                                                                    key={
                                                                        config.id
                                                                    }
                                                                    value={config.id.toString()}
                                                                >
                                                                    {
                                                                        config.name
                                                                    }
                                                                    {config.bed_count &&
                                                                    config.bath_count
                                                                        ? ` (${config.bed_count} bed, ${config.bath_count} bath)`
                                                                        : ''}
                                                                </MenuItem>
                                                            ),
                                                        )}
                                                    </Select>
                                                </FormControl>
                                            )
                                        }

                                        if (
                                            fieldName === 'preferred_premiums'
                                        ) {
                                            return (
                                                <GuestCardPremiumQuestion
                                                    key={fieldName}
                                                    className={
                                                        classes.formField
                                                    }
                                                    label={label}
                                                    required={fieldRequired(
                                                        fieldName,
                                                    )}
                                                    premiums={
                                                        apiData?.premiums || []
                                                    }
                                                    selectedIds={
                                                        selectedPremiumIds
                                                    }
                                                    noneSelected={premiumsNone}
                                                    onChange={({
                                                        selectedIds,
                                                        noneSelected,
                                                    }) => {
                                                        setSelectedPremiumIds(
                                                            selectedIds,
                                                        )
                                                        setPremiumsNone(
                                                            noneSelected,
                                                        )
                                                    }}
                                                />
                                            )
                                        }

                                        // Handle school_attendance as an autocomplete with search
                                        if (fieldName === 'school_attendance') {
                                            const selectedSchool =
                                                schoolList?.find(
                                                    (school) =>
                                                        school.id.toString() ===
                                                        formData[fieldName],
                                                ) || null

                                            return (
                                                <Autocomplete<
                                                    School,
                                                    false,
                                                    false,
                                                    false
                                                >
                                                    key={fieldName}
                                                    options={schoolList}
                                                    getOptionLabel={(
                                                        option: School,
                                                    ) =>
                                                        schoolDisplayName(
                                                            option,
                                                        )
                                                    }
                                                    value={selectedSchool}
                                                    onChange={(
                                                        event: any,
                                                        newValue: School | null,
                                                    ) => {
                                                        handleFieldChange(
                                                            fieldName,
                                                            newValue
                                                                ? newValue.id.toString()
                                                                : '',
                                                        )
                                                    }}
                                                    renderInput={(
                                                        params: any,
                                                    ) => (
                                                        <TextField
                                                            {...params}
                                                            className={
                                                                classes.formField
                                                            }
                                                            label={label}
                                                            variant="outlined"
                                                            fullWidth
                                                            placeholder="Search and select school..."
                                                            required={fieldRequired(
                                                                fieldName,
                                                            )}
                                                        />
                                                    )}
                                                    renderOption={(
                                                        option: School,
                                                    ) => (
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {schoolDisplayName(
                                                                    option,
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                />
                                            )
                                        }

                                        // Handle preferred move-in / move-out dates
                                        if (
                                            fieldName ===
                                                'preferred_move_in_date' ||
                                            fieldName ===
                                                'preferred_move_out_date'
                                        ) {
                                            return (
                                                <TextField
                                                    key={fieldName}
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    label={label}
                                                    value={
                                                        formData[fieldName] ||
                                                        ''
                                                    }
                                                    onChange={(e) =>
                                                        handleFieldChange(
                                                            fieldName,
                                                            e.target.value,
                                                        )
                                                    }
                                                    variant="outlined"
                                                    type="date"
                                                    required={fieldRequired(
                                                        fieldName,
                                                    )}
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                    inputProps={{
                                                        placeholder:
                                                            'MM/DD/YYYY',
                                                    }}
                                                />
                                            )
                                        }

                                        // Handle preferred_rent_cost as a number input
                                        if (
                                            fieldName === 'preferred_rent_cost'
                                        ) {
                                            return (
                                                <TextField
                                                    key={fieldName}
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    label={label}
                                                    value={
                                                        formData[fieldName] ||
                                                        ''
                                                    }
                                                    onChange={(e) =>
                                                        handleFieldChange(
                                                            fieldName,
                                                            e.target.value,
                                                        )
                                                    }
                                                    variant="outlined"
                                                    type="number"
                                                    required={fieldRequired(
                                                        fieldName,
                                                    )}
                                                    inputProps={{
                                                        min: 0,
                                                        step: 1,
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                $
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            )
                                        }

                                        return (
                                            <TextField
                                                key={fieldName}
                                                className={classes.formField}
                                                fullWidth
                                                label={label}
                                                value={
                                                    formData[fieldName] || ''
                                                }
                                                onChange={(e) =>
                                                    handleFieldChange(
                                                        fieldName,
                                                        e.target.value,
                                                    )
                                                }
                                                variant="outlined"
                                                type="text"
                                                required={fieldRequired(
                                                    fieldName,
                                                )}
                                            />
                                        )
                                    })}

                                {hasCustomQuestions &&
                                    [...guestCard.card_questions]
                                        .sort((a, b) => a.order - b.order)
                                        .map((cardQuestion) => (
                                            <CustomQuestionField
                                                key={
                                                    cardQuestion.custom_question
                                                        .id
                                                }
                                                question={
                                                    cardQuestion.custom_question
                                                }
                                                required={cardQuestion.required}
                                                value={
                                                    formData[
                                                        `custom_${cardQuestion.custom_question.id}`
                                                    ] || ''
                                                }
                                                onChange={(value) =>
                                                    handleFieldChange(
                                                        `custom_${cardQuestion.custom_question.id}`,
                                                        value,
                                                    )
                                                }
                                                className={classes.formField}
                                            />
                                        ))}

                                {/* Render preferred_roommates at the bottom, after custom questions */}
                                {additionalFields.includes(
                                    'preferred_roommates',
                                ) &&
                                    (() => {
                                        const fieldName = 'preferred_roommates'
                                        const label =
                                            GUEST_CARD_FIELD_QUESTIONS[
                                                fieldName
                                            ]

                                        // Handle preferred_roommates as a radio group with dynamic roommate entries
                                        return (
                                            <Box key={fieldName}>
                                                <FormControl
                                                    component="fieldset"
                                                    className={
                                                        classes.formField
                                                    }
                                                    fullWidth
                                                    required={fieldRequired(
                                                        fieldName,
                                                    )}
                                                >
                                                    <FormLabel component="legend">
                                                        {label}
                                                    </FormLabel>
                                                    <RadioGroup
                                                        value={hasRoommates}
                                                        onChange={(e) => {
                                                            const value =
                                                                e.target.value
                                                            setHasRoommates(
                                                                value,
                                                            )
                                                            if (
                                                                value === 'no'
                                                            ) {
                                                                setRoommates([])
                                                                handleFieldChange(
                                                                    fieldName,
                                                                    '',
                                                                )
                                                            } else {
                                                                // Initialize with one empty roommate entry
                                                                if (
                                                                    roommates.length ===
                                                                    0
                                                                ) {
                                                                    setRoommates(
                                                                        [
                                                                            {
                                                                                name:
                                                                                    '',
                                                                                email:
                                                                                    '',
                                                                                phone:
                                                                                    '',
                                                                            },
                                                                        ],
                                                                    )
                                                                }
                                                            }
                                                        }}
                                                        row
                                                    >
                                                        <FormControlLabel
                                                            value="no"
                                                            control={<Radio />}
                                                            label="No"
                                                        />
                                                        <FormControlLabel
                                                            value="yes"
                                                            control={<Radio />}
                                                            label="Yes"
                                                        />
                                                    </RadioGroup>
                                                </FormControl>

                                                {/* Roommate entries section */}
                                                {hasRoommates === 'yes' && (
                                                    <Box>
                                                        <Typography
                                                            variant="subtitle2"
                                                            style={{
                                                                marginBottom: theme.spacing(
                                                                    1,
                                                                ),
                                                                fontWeight: 600,
                                                                color:
                                                                    '#374151',
                                                                fontSize:
                                                                    '0.875rem',
                                                            }}
                                                        >
                                                            Roommate Information
                                                        </Typography>
                                                        {roommates.map(
                                                            (
                                                                roommate,
                                                                index,
                                                            ) => (
                                                                <Card
                                                                    key={index}
                                                                    style={{
                                                                        marginBottom: theme.spacing(
                                                                            1.5,
                                                                        ),
                                                                        padding: theme.spacing(
                                                                            2,
                                                                        ),
                                                                        backgroundColor:
                                                                            '#f9fafb',
                                                                        border:
                                                                            '1px solid #e5e7eb',
                                                                        borderRadius:
                                                                            '8px',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                        style={{
                                                                            marginBottom: theme.spacing(
                                                                                1,
                                                                            ),
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            style={{
                                                                                fontWeight: 600,
                                                                                fontSize:
                                                                                    '0.875rem',
                                                                                color:
                                                                                    '#1f2937',
                                                                            }}
                                                                        >
                                                                            Roommate{' '}
                                                                            {index +
                                                                                1}
                                                                        </Typography>
                                                                        {roommates.length >
                                                                            1 && (
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() => {
                                                                                    const updated = roommates.filter(
                                                                                        (
                                                                                            _,
                                                                                            i,
                                                                                        ) =>
                                                                                            i !==
                                                                                            index,
                                                                                    )
                                                                                    setRoommates(
                                                                                        updated,
                                                                                    )
                                                                                    // Update form data
                                                                                    const roommateData = updated.map(
                                                                                        (
                                                                                            r,
                                                                                            i,
                                                                                        ) => ({
                                                                                            [`roommate_${i}_name`]: r.name,
                                                                                            [`roommate_${i}_email`]: r.email,
                                                                                            [`roommate_${i}_phone`]: r.phone,
                                                                                        }),
                                                                                    )
                                                                                    const newFormData = {
                                                                                        ...formData,
                                                                                    }
                                                                                    // Remove old roommate data
                                                                                    Object.keys(
                                                                                        newFormData,
                                                                                    ).forEach(
                                                                                        (
                                                                                            key,
                                                                                        ) => {
                                                                                            if (
                                                                                                key.startsWith(
                                                                                                    'roommate_',
                                                                                                )
                                                                                            ) {
                                                                                                delete newFormData[
                                                                                                    key
                                                                                                ]
                                                                                            }
                                                                                        },
                                                                                    )
                                                                                    // Add new roommate data
                                                                                    roommateData.forEach(
                                                                                        (
                                                                                            data,
                                                                                        ) => {
                                                                                            Object.assign(
                                                                                                newFormData,
                                                                                                data,
                                                                                            )
                                                                                        },
                                                                                    )
                                                                                    setFormData(
                                                                                        newFormData,
                                                                                    )
                                                                                }}
                                                                                style={{
                                                                                    color:
                                                                                        '#ef4444',
                                                                                    padding: theme.spacing(
                                                                                        0.5,
                                                                                    ),
                                                                                }}
                                                                            >
                                                                                <DeleteIcon fontSize="small" />
                                                                            </IconButton>
                                                                        )}
                                                                    </Box>
                                                                    <TextField
                                                                        className={
                                                                            classes.formField
                                                                        }
                                                                        fullWidth
                                                                        label="Name"
                                                                        value={
                                                                            roommate.name
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated = [
                                                                                ...roommates,
                                                                            ]
                                                                            updated[
                                                                                index
                                                                            ].name =
                                                                                e.target.value
                                                                            setRoommates(
                                                                                updated,
                                                                            )
                                                                            handleFieldChange(
                                                                                `roommate_${index}_name`,
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }}
                                                                        variant="outlined"
                                                                        required
                                                                        style={{
                                                                            marginBottom: theme.spacing(
                                                                                1.5,
                                                                            ),
                                                                        }}
                                                                        size="small"
                                                                    />
                                                                    <TextField
                                                                        className={
                                                                            classes.formField
                                                                        }
                                                                        fullWidth
                                                                        label="Email"
                                                                        type="email"
                                                                        value={
                                                                            roommate.email
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const updated = [
                                                                                ...roommates,
                                                                            ]
                                                                            updated[
                                                                                index
                                                                            ].email =
                                                                                e.target.value
                                                                            setRoommates(
                                                                                updated,
                                                                            )
                                                                            handleFieldChange(
                                                                                `roommate_${index}_email`,
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                        }}
                                                                        variant="outlined"
                                                                        required
                                                                        style={{
                                                                            marginBottom: theme.spacing(
                                                                                1.5,
                                                                            ),
                                                                        }}
                                                                        size="small"
                                                                    />
                                                                    <TextField
                                                                        className={
                                                                            classes.formField
                                                                        }
                                                                        fullWidth
                                                                        label="Phone"
                                                                        type="tel"
                                                                        value={
                                                                            roommate.phone
                                                                        }
                                                                        onChange={(
                                                                            e,
                                                                        ) => {
                                                                            const normalized = normalizePhone(
                                                                                e
                                                                                    .target
                                                                                    .value,
                                                                            )
                                                                            const updated = [
                                                                                ...roommates,
                                                                            ]
                                                                            updated[
                                                                                index
                                                                            ].phone = normalized
                                                                            setRoommates(
                                                                                updated,
                                                                            )
                                                                            handleFieldChange(
                                                                                `roommate_${index}_phone`,
                                                                                normalized,
                                                                            )
                                                                        }}
                                                                        variant="outlined"
                                                                        required
                                                                        size="small"
                                                                    />
                                                                </Card>
                                                            ),
                                                        )}
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={
                                                                <AddIcon />
                                                            }
                                                            onClick={() => {
                                                                const updated = [
                                                                    ...roommates,
                                                                    {
                                                                        name:
                                                                            '',
                                                                        email:
                                                                            '',
                                                                        phone:
                                                                            '',
                                                                    },
                                                                ]
                                                                setRoommates(
                                                                    updated,
                                                                )
                                                            }}
                                                            style={{
                                                                margin: theme.spacing(
                                                                    1,
                                                                    0,
                                                                    2,
                                                                    0,
                                                                ),
                                                                textTransform:
                                                                    'none',
                                                                padding: theme.spacing(
                                                                    1,
                                                                    2,
                                                                ),
                                                                borderRadius:
                                                                    '8px',
                                                                borderColor:
                                                                    '#3b82f6',
                                                                color:
                                                                    '#3b82f6',
                                                                fontWeight: 500,
                                                                fontSize:
                                                                    '0.875rem',
                                                            }}
                                                        >
                                                            Add Another Roommate
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        )
                                    })()}
                            </>
                        )}

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            className={classes.submitButton}
                            disabled={submitGuestCardForm.isLoading}
                        >
                            {submitGuestCardForm.isLoading
                                ? 'Submitting...'
                                : 'Submit'}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </Box>
    )
}
